import React, { useState, useCallback, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Stack, TextField, InputLabel, Typography, Divider, Switch } from "@mui/material";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { GuestsStoreContext } from "../../Store/GuestsStore.ts";
import AvatarDropzone from "../StyledComponents/AvatarDropzone/AvatarDropzone";
import { DeleteForever, ArrowBack, FileDownload } from "@mui/icons-material";
import { exportDataToCsv } from "../../Utils/exportTableToCsv.js";
import { addOrUpdateGuest, deleteGuest, getGuestById } from "../../Requests/GuestService.ts";
import { Guest } from "../../Models/Guest.ts";
import CircularProgressView from "../StyledComponents/CircularProgressView.jsx";
import HtmlEditor from '../HtmlEditorView/HtmlEditor.jsx';


export default function GuestsInfo() {

  const navigate = useNavigate()
  const { guestid } = useParams()
  const store = useContext(GuestsStoreContext)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [avatar, setAvatar] = useState<string | undefined>("")
  const [firstName, setFirstName] = useState<string | undefined>("")
  const [lastName, setLastName] = useState<string | undefined>("")
  const [bio, setBio] = useState<string | undefined>("")
  const [hidden, setHidden] = useState<boolean | undefined>(false)
  const [createdAt, setCreatedAt] = useState<Date | undefined>()
  const [updatedAt, setUpdatedAt] = useState<Date | undefined>()
  const [occupation, setOccupation] = useState<string | undefined>("")

  const APP_BAR_HEIGHT = 64

  const handleSaveChanges = async (): Promise<void> => {
    let guest = new Guest()
    guest.objectId = guestid
    guest.firstName = firstName
    guest.lastName = lastName
    guest.bio = bio
    guest.hidden = hidden
    guest.occupation = occupation
    await addOrUpdateGuest(guest, avatar)
    // Mark store as needing refresh
    store.needsRefresh = true
    navigate("/guests")
  }

  const handleDeleteGuest = useCallback(async (): Promise<void> => {
    await deleteGuest(guestid)
    // Mark store as needing refresh
    store.needsRefresh = true
    navigate("/guests")
  }, [guestid, navigate, store])

  const fetchData = useCallback(async () => {
    const result = await getGuestById(guestid)

    setAvatar(result.avatar)
    setFirstName(result.firstName)
    setLastName(result.lastName)
    setBio(result.bio)
    setHidden(result.hidden)
    setCreatedAt(result.createdAt)
    setUpdatedAt(result.updatedAt)
    setOccupation(result.occupation)

    setIsLoading(false)
  }, [guestid])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const getGuestForExport = useCallback(() => ({
    objectId: guestid,
    avatar,
    firstName,
    lastName,
    bio,
    hidden,
    createdAt,
    updatedAt,
    occupation,
  }), [avatar, bio, createdAt, firstName, guestid, hidden, lastName, occupation, updatedAt])

  const loaderView = <CircularProgressView />

  const contentView = <>
    <Box
      sx={{
        position: "sticky",
        top: APP_BAR_HEIGHT,
        backgroundColor: "background.paper",
        zIndex: 100
      }}
    >
      <Box
        paddingTop={2}
        paddingBottom={2}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          gap: 1,
          width: "calc(100% - 280px)",
          paddingRight: 2
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate("/guests")}
            sx={{ minWidth: "auto", alignSelf: "flex-start" }}
          >
            Back
          </Button>
          <Typography
            variant="h4"
            sx={{
              maxWidth: "600px"
            }}
          >
            {firstName + " " + lastName}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          onClick={() => handleSaveChanges()}
        >
          Save Changes
        </Button>
      </Box>
      <Divider />
    </Box>


    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between"
      }}
    >
      <Box
        width="calc(100% - 280px)"
        sx={{
          paddingRight: 2
        }}
      >
        <AvatarDropzone
          title={"Guest Avatar"}
          avatarUrl={avatar}
          callbackFile={(file) => { setAvatar(file) }}
        />

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"First Name"}</InputLabel>
          <TextField
            placeholder="First Name"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
          />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Last Name"}</InputLabel>
          <TextField
            placeholder="Last Name"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
          />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Occupation"}</InputLabel>
          <TextField
            placeholder="Occupation"
            value={occupation}
            onChange={(event) => setOccupation(event.target.value)}
          />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Bio"}</InputLabel>
          <HtmlEditor initialValue={bio} onChange={setBio} />
        </Stack>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "280px",
          position: "fixed",
          right: 0,
          top: APP_BAR_HEIGHT + 1,
          bottom: 0,
          overflowY: "auto",
          backgroundColor: "background.paper",
          borderLeft: "1px solid",
          borderColor: "grey.200",
          zIndex: 110
        }}
      >
        <Divider orientation="vertical" />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            padding: 2
          }}
        >
          <Button
            size="small"
            startIcon={<FileDownload />}
            onClick={() =>
              exportDataToCsv([getGuestForExport()], guestid ? `guest-${guestid}` : "guest", {
                preferredKeys: ["firstName", "lastName"],
              })
            }
            sx={{ mb: 2, alignSelf: "flex-start" }}
          >
            Export CSV
          </Button>
          <Box
            width="100%"
            display="flex"
            justifyContent="space-between"
          >
            <InputLabel>{"Date Created"}</InputLabel>
            <InputLabel>{dayjs(createdAt).format('DD/MMM/YYYY')}</InputLabel>
          </Box>

          <Box
            marginTop={1}
            width="100%"
            display="flex"
            justifyContent="space-between"
          >
            <InputLabel>{"Last Update"}</InputLabel>
            <InputLabel>{dayjs(updatedAt).format('DD/MMM/YYYY')}</InputLabel>
          </Box>

          <Box
            marginTop={1}
            marginBottom={2}
            width="100%"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <InputLabel>{"Visibility"}</InputLabel>
            <Switch
              checked={!hidden}
              onChange={(event) => setHidden(!event.target.checked)}
            />
          </Box>

          <Divider />

          <Box
            sx={{
              marginTop: 2,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            <Button
              variant="outlined"
              color="error"
              onClick={() => handleDeleteGuest()}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1
                }}
              >
                <DeleteForever />
                Delete Guest
              </Box>
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  </>

  return (
    <>
      {isLoading ? loaderView : contentView}
    </>
  )
}
